/**
 * Email Validation Utility
 * Shared validation logic for business email validation with testing exceptions
 */

/**
 * Validates basic email format
 */
export function isValidEmailFormat(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Checks if email domain is a free email provider (personal email)
 */
export function isFreeEmailProvider(email: string): boolean {
  if (!email || !email.includes('@')) return false;
  
  const freeProviders = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'protonmail.com',
    'live.com',
    'msn.com',
    'yandex.com'
  ];
  
  const domain = email.split('@')[1]?.toLowerCase();
  return freeProviders.includes(domain);
}

/**
 * Checks if email is a testing email (ispp07* Gmail accounts)
 * Allows any Gmail address starting with ispp07 (e.g., ispp07+9@gmail.com, ispp07+test@gmail.com)
 */
export function isTestingEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  
  const emailLower = email.toLowerCase().trim();
  return emailLower.startsWith('ispp07') && emailLower.endsWith('@gmail.com');
}

/**
 * Validates if an email is a business email (not free email providers)
 * Includes exception for testing emails (ispp07* Gmail accounts)
 */
export function isBusinessEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  
  // Allow testing emails (ispp07* Gmail accounts)
  if (isTestingEmail(email)) {
    return true;
  }
  
  // Check if it's a free email provider (personal email)
  if (isFreeEmailProvider(email)) {
    return false;
  }
  
  // Passed all checks - it's a business email
  return true;
}

/**
 * Comprehensive email validation for business email requirement
 * Returns object with validation result and error message
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateBusinessEmail(email: string): EmailValidationResult {
  // Check if email is provided
  if (!email || email.trim().length === 0) {
    return {
      isValid: false,
      error: 'Email is required'
    };
  }
  
  // Validate email format
  if (!isValidEmailFormat(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }
  
  // Validate business email requirement
  if (!isBusinessEmail(email)) {
    return {
      isValid: false,
      error: 'Please use a business email address. Personal email providers (Gmail, Yahoo, Hotmail, etc.) are not accepted.'
    };
  }
  
  // All validations passed
  return {
    isValid: true
  };
}